/**
 * Project mutation hooks using factory pattern with realtime broadcast
 * Reduced from 173 lines to ~60 lines (65% reduction)
 * All features preserved: create, update, updateField, delete, realtime sync
 */

import type {
  DeleteProjectOptions,
  MergeProjectsRequest,
} from '@/lib/api/projects.api'
import { projectsApi } from '@/lib/api/projects.api'
import { postMessage } from '@/lib/realtime/broadcast'
import { scopedProjectKey } from '@/lib/realtime/project-query-scope'
import { buildUpdatePayload } from '@/utils/saveCustomField'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { CreateProject, ProjectWithEntity } from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import { projectModuleCountsKey } from '../useProjectModuleCounts'
import { useWorkspaceSession } from '../useWorkspaceSession'
import { createResourceMutations } from './createResourceMutations'

// Define types for the factory
type ProjectCreate = CreateProject & { organizationId: string }
type ProjectUpdate = Partial<ProjectWithEntity>

// Core mutations using factory with realtime enabled
function projectMutations(scope: ReturnType<typeof useWorkspaceSession>) {
  async function run<T>(event: string, request: () => Promise<T>, id?: string) {
    const result = await scope.run(new AbortController().signal, request)
    const record = result as
      | { id?: string; organizationId?: string }
      | undefined
    postMessage(event, {
      id: id ?? record?.id,
      organizationId: record?.organizationId,
      eventId: crypto.randomUUID(),
    })
    return result
  }
  return createResourceMutations<
    ProjectWithEntity,
    ProjectCreate,
    ProjectUpdate
  >({
    resourceName: 'Project',
    resourceNameSingular: 'project',
    queryKeyFactory: {
      ...queryKeys.projects,
      lists: () => scopedProjectKey(queryKeys.projects.lists(), scope),
      detail: (id: string, options?: { includeCustomFields?: boolean }) =>
        scopedProjectKey(queryKeys.projects.detail(id, options), scope),
    },
    api: {
      create: (data: ProjectCreate) =>
        run('project:created', () => projectsApi.createProject(data)),
      update: (id: string, data: ProjectUpdate) =>
        run('project:updated', () => projectsApi.updateProject(id, data), id),
      updateField: (id: string, field: string, value: unknown) => {
        const updateData = buildUpdatePayload(field, value)
        return run(
          'project:updated',
          () => projectsApi.updateProject(id, updateData),
          id,
        )
      },
      delete: (id: string) =>
        run('project:deleted', () => projectsApi.deleteProject(id), id),
    },
    enableRealtime: false, // Project notices above deliberately contain no field values.
    deferredEntityType: 'project',
    invalidation: {
      onCreate: { invalidateRelated: [[...projectModuleCountsKey]] },
      onDelete: { invalidateRelated: [[...projectModuleCountsKey]] },
    },
  })
}

// Export create mutation with wrapper for backward-compatible API signature
export function useCreateProjectMutation() {
  const mutation = projectMutations(useWorkspaceSession()).useCreateMutation()
  return {
    ...mutation,
    mutate: ({
      projectData,
      organizationId,
    }: {
      projectData: CreateProject
      organizationId: string
    }) => mutation.mutate({ data: { ...projectData, organizationId } }),
    mutateAsync: ({
      projectData,
      organizationId,
    }: {
      projectData: CreateProject
      organizationId: string
    }) => mutation.mutateAsync({ data: { ...projectData, organizationId } }),
  }
}

export function useUpdateProjectMutation() {
  return projectMutations(useWorkspaceSession()).useUpdateMutation()
}
export function useUpdateProjectFieldMutation() {
  return projectMutations(useWorkspaceSession()).useUpdateFieldMutation!()
}
export function useDeleteProjectMutation() {
  return projectMutations(useWorkspaceSession()).useDeleteMutation()
}

/**
 * Delete mutation that supports linked item options (reassign/unlink/delete per category)
 */
export function useDeleteProjectWithOptionsMutation() {
  return useMutation({
    mutationFn: ({
      id,
      options,
    }: {
      id: string
      options?: DeleteProjectOptions
    }) => projectsApi.deleteProject(id, options),
    onSuccess: () => {
      // Query cleanup is handled by the caller after the deletion animation completes
    },
    onError: () => {
      toast.error('Failed to delete project. Please try again.')
    },
  })
}

export function useMergeProjectsMutation() {
  return useMutation({
    mutationFn: (data: MergeProjectsRequest) => projectsApi.mergeProjects(data),
    onError: () => {
      toast.error('Failed to merge projects. Please try again.')
    },
  })
}
